import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, Copy, Check, Loader2, UserSquare, Building2 } from "lucide-react";

/** Generates a buyer-attributed share link for the active report.
 *  Pick a Cash Buyer (or any team member) and copy the link — every photo
 *  view on the resulting public report will be attributed back to them.
 */
export default function BuyerLinkPicker({ leadId, shareToken, shareEnabled }) {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState("");
  const [shortlink, setShortlink] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/team");
      // Surface buyers first, then others.
      const sorted = (r.data || []).sort((a, b) => {
        const aBuyer = (a.role || "").toLowerCase().includes("buyer") ? 0 : 1;
        const bBuyer = (b.role || "").toLowerCase().includes("buyer") ? 0 : 1;
        return aBuyer - bBuyer || (a.name || "").localeCompare(b.name || "");
      });
      setTeam(sorted);
    } catch {
      setTeam([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!picked) return;
    setBusy(true);
    setShortlink(null);
    setCopied(false);
    try {
      const r = await api.post("/shortlinks", {
        lead_id: leadId,
        kind: "report",
        target_token: shareToken,
        team_member_id: picked,
      });
      setShortlink(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create link");
    } finally {
      setBusy(false);
    }
  };

  const onCopy = async () => {
    if (!shortlink?.redirect_url) return;
    try {
      const url = `${shortlink.redirect_url}?utm_source=buyer_link`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      const buyer = team.find((t) => t.id === picked);
      toast.success(`Buyer link for ${buyer?.name || "buyer"} copied`);
    } catch { /* ignore */ }
  };

  if (!shareEnabled || !shareToken) {
    return null;
  }

  return (
    <div className="card p-4 mb-4 print:hidden" data-testid="buyer-link-picker">
      <div className="flex items-start gap-3 mb-3">
        <span className="w-9 h-9 rounded-lg bg-violet-50 text-violet-700 flex items-center justify-center flex-shrink-0">
          <Send size={15} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="label-eyebrow">Send to a specific buyer</div>
          <div className="text-sm text-[var(--ink-2)] mt-0.5">
            Pick a buyer from your Power Team — we'll generate a tracked link that attributes every photo open and report visit back to them.
          </div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap items-stretch">
        <select
          className="input flex-1 min-w-[200px]"
          value={picked}
          onChange={(e) => { setPicked(e.target.value); setShortlink(null); }}
          disabled={loading}
          data-testid="buyer-link-select"
        >
          <option value="">— pick a contact —</option>
          {team.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} {t.role ? `· ${t.role}` : ""}{t.company ? ` (${t.company})` : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn-money"
          onClick={generate}
          disabled={!picked || busy}
          data-testid="buyer-link-generate"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {busy ? "Creating…" : "Generate"}
        </button>
      </div>

      {team.length === 0 && !loading && (
        <div className="text-xs text-[var(--ink-3)] mt-2 italic">
          No team contacts yet — add Cash Buyers from <strong>Power Team</strong> to enable attribution.
        </div>
      )}

      {shortlink && (
        <div
          className="mt-3 rounded-lg border p-3 flex items-center gap-2 bg-emerald-50/40"
          style={{ borderColor: "#86efac" }}
          data-testid="buyer-link-result"
        >
          <UserSquare size={14} className="text-emerald-700 flex-shrink-0" />
          <div className="font-mono text-xs truncate flex-1" title={shortlink.redirect_url}>
            {shortlink.redirect_url}
          </div>
          <button
            type="button"
            className="btn-outline !py-1 !px-2 text-xs flex-shrink-0"
            onClick={onCopy}
            data-testid="buyer-link-copy"
          >
            {copied ? <Check size={11} /> : <Copy size={11} />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      )}
    </div>
  );
}
